import { Badge, Card, ProgressBar } from 'react-bootstrap';
import { FaCheckCircle, FaClock, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';

/**
 * Componente para exibir status da assinatura
 */
export const SubscriptionStatus = ({ subscription, isOnTrial, trialEndsAt, isPendingCancellation, accessUntil }) => {
  const statusConfig = {
    active: {
      icon: <FaCheckCircle />,
      bg: 'success',
      text: 'Ativa',
      description: 'Sua assinatura está ativa e em dia'
    },
    cancelled: {
      icon: <FaTimesCircle />,
      bg: 'danger',
      text: 'Cancelada',
      description: 'Assinatura cancelada'
    },
    suspended: {
      icon: <FaExclamationTriangle />,
      bg: 'warning',
      text: 'Suspensa',
      description: 'Pagamento pendente'
    },
    expired: {
      icon: <FaClock />,
      bg: 'secondary',
      text: 'Expirada',
      description: 'Assinatura expirada'
    }
  };

  const status = statusConfig[subscription?.status] || statusConfig.active;

  // Calcular dias restantes do trial
  const calculateTrialDaysRemaining = () => {
    if (!isOnTrial || !trialEndsAt) return 0;
    const today = new Date();
    const endDate = new Date(trialEndsAt);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const trialDaysRemaining = calculateTrialDaysRemaining();
  const trialProgress = isOnTrial ? ((30 - trialDaysRemaining) / 30) * 100 : 0;

  // Se está com cancelamento pendente, mostrar aviso
  if (isPendingCancellation && accessUntil) {
    return (
      <Card className="mb-4 border-warning">
        <Card.Body>
          <div className="d-flex align-items-center mb-3">
            <FaExclamationTriangle className="text-warning me-2" size={24} />
            <div>
              <h5 className="mb-0">Assinatura Cancelada</h5>
              <small className="text-muted">
                Você ainda tem acesso até {new Date(accessUntil).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </small>
            </div>
          </div>
          <p className="mb-0 small text-muted">
            Sua assinatura foi cancelada, mas você ainda pode utilizar todos os recursos do plano contratado até a data final de acesso.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (isOnTrial) {
    return (
      <Card className="mb-4 border-info">
        <Card.Body>
          <div className="d-flex align-items-center mb-3">
            <FaClock className="text-info me-2" size={24} />
            <div>
              <h5 className="mb-0">Período de Trial</h5>
              <small className="text-muted">
                {trialDaysRemaining} dias restantes de 30
              </small>
            </div>
          </div>
          <ProgressBar 
            now={trialProgress} 
            variant="info" 
            label={`${Math.round(trialProgress)}%`}
            className="mb-2"
          />
          <p className="mb-0 small text-muted">
            Seu trial termina em {new Date(trialEndsAt).toLocaleDateString('pt-BR')}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={`mb-4 border-${status.bg}`}>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <span className={`text-${status.bg} me-2`} style={{ fontSize: '1.5rem' }}>
              {status.icon}
            </span>
            <div>
              <h6 className="mb-0">Status da Assinatura</h6>
              <Badge bg={status.bg}>{status.text}</Badge>
            </div>
          </div>
          <div className="text-end">
            <small className="text-muted d-block">
              {status.description}
            </small>
            {subscription?.next_due_date && status.bg === 'success' && (
              <small className="text-muted">
                Próximo vencimento: {new Date(subscription.next_due_date).toLocaleDateString('pt-BR')}
              </small>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
